/**
 * Lindy - Banner Carousel Component
 *
 * A custom element for banner carousel with autoplay and navigation
 */

class BannerCarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.autoplayTimer = null;
    this.isHovered = false;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.offset = 0;
    this.track = null;
    this.velocity = 0;
    this.lastX = 0;
    this.lastTime = 0;
    this.momentumTimer = null;
    this.hasDragged = false;
    this.dragThreshold = 5;
  }

  connectedCallback() {
    this.track = this.querySelector('.banner-carousel__track');
    this.slides = Array.from(this.querySelectorAll('.banner-carousel__slide'));
    this.dots = Array.from(this.querySelectorAll('.banner-carousel__dot'));
    this.prevButton = this.querySelector('.banner-carousel__nav-button--prev');
    this.nextButton = this.querySelector('.banner-carousel__nav-button--next');

    this.autoplay = this.dataset.autoplay === 'true';
    // @ts-ignore
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) * 1000 || 5000;

    if (this.slides.length === 0) return;

    this.init();
  }

  init() {
    // Show first slide
    this.showSlide(0);

    // Add event listeners for navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previousSlide());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Add event listeners for dots
    // @ts-ignore
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Pause autoplay on hover
    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.stopAutoplay();
    });

    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
      if (this.autoplay) {
        this.startAutoplay();
      }
    });

    // Start autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }

    // Pause autoplay when tab is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay && !this.isHovered) {
        this.startAutoplay();
      }
    });

    // Support keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.previousSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });

    // Support touch swipe on mobile
    this.setupTouchSwipe();
    
    // Prevent link click when dragging
    this.setupLinkPrevention();
  }

  // @ts-ignore
  showSlide(index, animate = true) {
    if (!this.track) return;

    // @ts-ignore
    const slideWidth = this.track.offsetWidth;
    const translateX = -index * slideWidth + (this.isDragging ? this.offset : 0);

    // @ts-ignore
    this.track.style.transform = `translateX(${translateX}px)`;

    if (!this.isDragging) {
      this.offset = 0;
      this.updateActiveSlide(index);
    }

    this.currentSlide = index;
  }

  // @ts-ignore
  updateActiveSlide(index) {
    const visibleIndex = this.getVisibleSlideIndex();
    const activeIndex = Math.round(visibleIndex);

    // @ts-ignore
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === activeIndex);
    });

    // @ts-ignore
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('banner-carousel__dot--active', i === activeIndex);
    });
  }

  getVisibleSlideIndex() {
    if (!this.track) return this.currentSlide;
    // @ts-ignore
    const slideWidth = this.track.offsetWidth;
    const translateX = -this.currentSlide * slideWidth + this.offset;
    return -translateX / slideWidth;
  }

  updateTransform() {
    if (!this.track) return;
    // @ts-ignore
    const slideWidth = this.track.offsetWidth;
    const translateX = -this.currentSlide * slideWidth + this.offset;
    // @ts-ignore
    this.track.style.transform = `translateX(${translateX}px)`;
    
    if (this.isDragging) {
      const visibleIndex = this.getVisibleSlideIndex();
      const activeIndex = Math.round(visibleIndex);
      
      // @ts-ignore
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('active', i === activeIndex);
      });

      // @ts-ignore
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('banner-carousel__dot--active', i === activeIndex);
      });
    }
  }

  nextSlide() {
    // @ts-ignore
    const next = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(next);
  }

  previousSlide() {
    // @ts-ignore
    const prev = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prev);
  }

  // @ts-ignore
  goToSlide(index) {
    this.stopMomentum();
    this.showSlide(index);
    this.updateActiveSlide(index);

    // Reset autoplay timer when manually navigating
    if (this.autoplay && !this.isHovered) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  setupTouchSwipe() {
    this.addEventListener('touchstart', (e) => {
      // @ts-ignore
      this.startDrag(e.changedTouches[0].screenX);
    }, { passive: false });

    this.addEventListener('touchmove', (e) => {
      if (this.isDragging) {
        e.preventDefault();
        // @ts-ignore
        this.drag(e.changedTouches[0].screenX);
      }
    }, { passive: false });

    this.addEventListener('touchend', () => {
      this.endDrag();
    }, { passive: true });

    this.addEventListener('touchcancel', () => {
      this.endDrag();
    }, { passive: true });

    this.addEventListener('mousedown', (e) => {
      this.startDrag(e.clientX);
      e.preventDefault();
    });

    this.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        this.drag(e.clientX);
      }
    });

    this.addEventListener('mouseup', () => {
      this.endDrag();
    });

    this.addEventListener('mouseleave', () => {
      this.endDrag();
    });
  }

  // @ts-ignore
  startDrag(x) {
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = x;
    this.currentX = x;
    this.lastX = x;
    this.offset = 0;
    this.velocity = 0;
    this.lastTime = Date.now();
    
    if (this.track) {
      this.track.classList.add('is-dragging');
    }

    this.stopAutoplay();
    this.stopMomentum();
  }

  // @ts-ignore
  drag(x) {
    if (!this.isDragging || !this.track) return;

    const now = Date.now();
    const deltaTime = now - this.lastTime;
    
    if (deltaTime > 0) {
      const deltaX = x - this.lastX;
      this.velocity = deltaX / deltaTime;
    }

    this.currentX = x;
    this.lastX = x;
    this.lastTime = now;
    
    const diff = this.currentX - this.startX;
    
    if (!this.hasDragged && Math.abs(diff) > this.dragThreshold) {
      this.hasDragged = true;
    }
    
    // @ts-ignore
    const slideWidth = this.track.offsetWidth;
    
    let resistance = 1;
    const resistanceFactor = 0.3;

    if (diff > 0 && this.currentSlide === 0) {
      resistance = Math.max(0.3, 1 - Math.abs(diff) / (slideWidth * resistanceFactor));
    // @ts-ignore
    } else if (diff < 0 && this.currentSlide === this.slides.length - 1) {
      resistance = Math.max(0.3, 1 - Math.abs(diff) / (slideWidth * resistanceFactor));
    }

    this.offset = diff * resistance;
    this.updateTransform();
  }

  endDrag() {
    if (!this.isDragging || !this.track) return;

    // @ts-ignore
    const slideWidth = this.track.offsetWidth;
    const diff = this.currentX - this.startX;
    const threshold = slideWidth * 0.25;
    const velocityThreshold = 0.3;

    const wasDragging = this.hasDragged;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    const shouldChange = Math.abs(diff) > threshold || Math.abs(this.velocity) > velocityThreshold;

    if (shouldChange) {
      const velocityDirection = this.velocity < -velocityThreshold ? -1 : this.velocity > velocityThreshold ? 1 : 0;
      const dragDirection = diff > threshold ? 1 : diff < -threshold ? -1 : 0;
      const direction = velocityDirection !== 0 ? velocityDirection : dragDirection;

      if (direction > 0 && this.currentSlide > 0) {
        this.previousSlide();
      // @ts-ignore
      } else if (direction < 0 && this.currentSlide < this.slides.length - 1) {
        this.nextSlide();
      } else {
        this.startMomentum(diff);
      }
    } else {
      this.startMomentum(diff);
    }

    if (wasDragging) {
      setTimeout(() => {
        this.hasDragged = false;
      }, 100);
    }

    if (this.autoplay && !this.isHovered) {
      this.startAutoplay();
    }
  }
  
  setupLinkPrevention() {
    const links = this.querySelectorAll('.banner-carousel__link');
    
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        if (this.hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }, true);
    });
  }

  // @ts-ignore
  startMomentum(diff) {
    this.stopMomentum();
    
    const momentumPower = Math.abs(this.velocity) * 200;
    const maxMomentum = 300;
    const momentum = Math.min(maxMomentum, momentumPower);
    
    if (Math.abs(momentum) < 10) {
      this.offset = 0;
      this.updateTransform();
      this.updateActiveSlide(this.currentSlide);
      return;
    }

    const direction = this.velocity < 0 ? -1 : 1;
    const targetOffset = diff + (momentum * direction);
    // @ts-ignore
    const slideWidth = this.track?.offsetWidth || 0;
    const targetIndex = Math.round((-this.currentSlide * slideWidth + targetOffset) / slideWidth);
    
    let finalIndex = this.currentSlide;
    if (targetIndex < this.currentSlide && this.currentSlide > 0) {
      finalIndex = this.currentSlide - 1;
    // @ts-ignore
    } else if (targetIndex > this.currentSlide && this.currentSlide < this.slides.length - 1) {
      finalIndex = this.currentSlide + 1;
    }

    const startOffset = this.offset;
    const targetSlideOffset = -finalIndex * slideWidth;
    const startTime = Date.now();
    const duration = 300;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      if (progress < 1) {
        this.offset = startOffset + (targetSlideOffset - (-this.currentSlide * slideWidth + startOffset)) * ease;
        this.updateTransform();
        this.momentumTimer = requestAnimationFrame(animate);
      } else {
        this.offset = 0;
        if (finalIndex !== this.currentSlide) {
          this.currentSlide = finalIndex;
        }
        this.updateTransform();
        this.updateActiveSlide(this.currentSlide);
      }
    };

    this.momentumTimer = requestAnimationFrame(animate);
  }

  stopMomentum() {
    if (this.momentumTimer) {
      cancelAnimationFrame(this.momentumTimer);
      this.momentumTimer = null;
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.stopMomentum();
  }
}

// Register the custom element
if (!customElements.get('banner-carousel-component')) {
  customElements.define('banner-carousel-component', BannerCarouselComponent);
}
